import type * as zod from 'zod';

import { getCommandSuccessResponse } from '@sb/integrations/implementations/ModbusTCPServer/command/util';
import { ModbusFunctionCodeEnum } from '@sb/integrations/utils/modbus/ModbusFunctionCode';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { wait } from '@sb/utilities';

import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import { NetworkRequestArguments } from './Arguments';
import Variables from './Variables';

type Arguments = zod.infer<typeof NetworkRequestArguments>;

type Variables = zod.infer<typeof Variables>;

export default class NetworkRequestStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = NetworkRequestArguments;

  public static Variables = Variables;

  public paused: boolean = false;

  public stepDone: boolean = false;

  public initializeVariableState(): void {
    this.variables = {};
  }

  public async _play({ fail, pauseRoutine }: StepPlayArguments): Promise<void> {
    this.paused = false;
    this.stepDone = false;

    const { command } = this.args;

    const { request } = command;

    while (!this.stepDone) {
      if (this.paused) {
        await wait(100);
        continue;
      }

      try {
        const responseData =
          await this.routineContext.sendNetworkRequest(command);

        const response = getCommandSuccessResponse(
          command.request.kind,
          responseData,
        );

        if (
          request.kind === ModbusFunctionCodeEnum.ReadHoldingRegisters ||
          request.kind === ModbusFunctionCodeEnum.ReadCoils ||
          request.kind === ModbusFunctionCodeEnum.ReadDiscreteInputs ||
          request.kind === ModbusFunctionCodeEnum.ReadInputRegisters
        ) {
          // TODO: APPS-4523 Add support for batch requests
          const [variableID] = request.variableIDList;

          const varState = this.routineFrame.getVariable(variableID);

          if (varState.stepKind !== 'EnvironmentVariable') {
            await fail({
              failure: {
                kind: FailureKind.StepPlayFailure,
                stepKind: this.getStepKind(),
              },
              failureReason: `Variable ID ${variableID} is not an environment variable`,
            });

            return;
          }

          this.routineFrame.setVariable(variableID, {
            ...varState,
            value: response.responseData,
          });
        }

        // Set stepDone after successful request (for both read and write operations)
        this.stepDone = true;
      } catch (error) {
        await pauseRoutine({
          kind: 'networkRequestFailure',
          reason: error.message,
        });
      }
    }
  }

  public _pause(): void {
    this.paused = true;
  }

  public _resume(): void {
    this.paused = false;
  }

  public _stop(): void {
    this.stepDone = true;
  }
}
